import React, { useState, useMemo } from 'react';
import ScheduleModal from './ScheduleModal';
import ResponsiveTable from '../../components/ResponsiveTable';
import styles from '../../styles/AsignacionGruposStyles/AsignacionGrupos.module.css';
import extendedStyles from '../../styles/AsignacionGruposStyles/AsignacionGruposExtended.module.css';

const MATERIAS_PER_PAGE = 15;
const GRUPOS_CARD_THRESHOLD = 5; // Si hay más de 5 grupos, usar tabla responsive

function StudentDetail({ 
  student, 
  onBack, 
  onAsignar, 
  onCancelar,
  onDesasignar,
  gruposDisponibles = [],
  gruposSeleccionados = [],
  onGrupoToggle,
  inscripcionActual,
  siguienteCiclo,
  loading,
  error,
  successMessage,
  formatModalidad
}) {
  const [showSchedule, setShowSchedule] = useState(false);
  const [expandedMaterias, setExpandedMaterias] = useState({});
  const [pageMaterias, setPageMaterias] = useState(1);
  const [confirmDesasignar, setConfirmDesasignar] = useState(null);

  // Función para formatear valores (evitar undefined/null)
  const formatValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return '—';
    }
    return String(value);
  };

  const getGroupStatusColor = (disponible) => {
    return disponible ? '#4CAF50' : '#F44336'; // Verde para disponible, rojo para no disponible
  };

  const getGroupStatusIcon = (disponible) => {
    return disponible ? '●' : '●';
  };

  // Toggle expansión de materia para ver grupos disponibles
  const toggleMateriaExpansion = (materiaId) => {
    setExpandedMaterias(prev => ({
      ...prev,
      [materiaId]: !prev[materiaId]
    }));
  };

  // Obtener grupos disponibles para una materia específica
  const getGruposParaMateria = (idMateria) => {
    const idMateriaNum = Number(idMateria);
    const grupos = gruposDisponibles.filter(g => Number(g.id_materia) === idMateriaNum);
    console.log(`[StudentDetail] getGruposParaMateria(${idMateria}):`, {
      idMateriaNum,
      gruposDisponiblesTotal: gruposDisponibles.length,
      gruposDisponibles: gruposDisponibles.map(g => ({ id: g.id, id_materia: g.id_materia, nombre: g.nombre_grupo })),
      gruposFiltrados: grupos.length
    });
    return grupos;
  };

  // Función para manejar el botón Cancelar
  const handleCancel = () => {
    if (onCancelar) {
      onCancelar();
    } else {
      onBack();
    }
  };

  // Función para solicitar confirmación de desasignación
  const handleDesasignarClick = (materia) => {
    setConfirmDesasignar({
      idEstudianteGrupo: materia.grupoAsignado?.id_estudiante_grupo || materia.grupoAsignado?.id,
      materiaNombre: materia.nombre,
      grupoCodigo: materia.grupoAsignado?.grupo_codigo || ''
    });
  };

  // Función para confirmar la desasignación
  const handleConfirmDesasignar = () => {
    if (confirmDesasignar && onDesasignar) {
      onDesasignar(confirmDesasignar.idEstudianteGrupo, confirmDesasignar.materiaNombre);
    }
    setConfirmDesasignar(null);
  };

  // Función para manejar el botón Asignar
  const handleAssign = () => {
    if (onAsignar) {
      onAsignar();
    } else {
      console.log('[StudentDetail] Botón Asignar presionado - sin acción implementada');
    }
  };

  // Renderizar grupos expandidos - con tabla responsive si hay muchos grupos
  const renderGruposExpandidos = (gruposParaMateria, materia) => {
    if (gruposParaMateria.length === 0) return null;

    // Si hay más de GRUPOS_CARD_THRESHOLD grupos, usar tabla responsive
    if (gruposParaMateria.length > GRUPOS_CARD_THRESHOLD) {
      const columns = [
        { 
          key: 'selection', 
          label: '✓', 
          width: '50px', 
          priority: 'high',
          align: 'center'
        },
        { 
          key: 'codigo_grupo', 
          label: 'Código', 
          priority: 'high',
          minWidth: '100px'
        },
        { 
          key: 'id_grupo', 
          label: 'ID Grupo', 
          priority: 'normal',
          minWidth: '80px'
        },
        { 
          key: 'horario', 
          label: 'Horario', 
          priority: 'normal',
          minWidth: '120px'
        },
        { 
          key: 'asesor', 
          label: 'Profesor', 
          priority: 'normal',
          minWidth: '120px'
        },
        { 
          key: 'cupo', 
          label: 'Cupo', 
          priority: 'normal',
          minWidth: '100px',
          align: 'center'
        },
        { 
          key: 'estado', 
          label: 'Estado', 
          priority: 'high',
          minWidth: '100px',
          align: 'center'
        }
      ];

      const renderCell = (grupo, column) => {
        switch (column.key) {
          case 'selection':
            return (
              <label className={extendedStyles.grupoCheckbox}>
                <input
                  type="checkbox"
                  checked={gruposSeleccionados.includes(grupo.id)}
                  onChange={() => onGrupoToggle && onGrupoToggle(grupo.id)}
                  disabled={loading}
                />
                <span className={extendedStyles.checkmark}></span>
              </label>
            );
          case 'codigo_grupo':
            return grupo.codigo_grupo || grupo.codigo || `Grupo ${grupo.id}`;
          case 'id_grupo':
            return grupo.id_grupo || grupo.id;
          case 'horario':
            return grupo.horario || 'Por definir';
          case 'asesor':
            return grupo.asesor || grupo.docente || '—';
          case 'cupo':
            return grupo.cupo || `${grupo.inscritos || 0}/${grupo.cupo_maximo || 'N/A'}`;
          case 'estado':
            return (
              <span 
                style={{ 
                  color: getGroupStatusColor(grupo.estado === 'ABIERTO'),
                  fontWeight: 600
                }}
              >
                {grupo.estado || 'ABIERTO'}
              </span>
            );
          default:
            return '—';
        }
      };

      return (
        <div className={extendedStyles.gruposExpandidos}>
          <div className={extendedStyles.gruposHeader}>
            <span>Grupos disponibles para {materia.nombre} ({gruposParaMateria.length})</span>
          </div>
          <ResponsiveTable
            columns={columns}
            data={gruposParaMateria}
            renderCell={renderCell}
            className={extendedStyles.gruposTable}
          />
        </div>
      );
    }

    // Para pocos grupos, mostrar tarjetas
    return (
      <div className={extendedStyles.gruposExpandidos}>
        <div className={extendedStyles.gruposHeader}>
          <span>Grupos disponibles para {materia.nombre}</span>
        </div>
        {gruposParaMateria.map(grupo => (
          <div key={grupo.id} className={extendedStyles.grupoOption}>
            <label className={extendedStyles.grupoCheckbox}>
              <input
                type="checkbox"
                checked={gruposSeleccionados.includes(grupo.id)}
                onChange={() => onGrupoToggle && onGrupoToggle(grupo.id)}
                disabled={loading}
              />
              <span className={extendedStyles.checkmark}></span>
            </label>
            <div className={extendedStyles.grupoInfo}>
              <span 
                className={extendedStyles.groupStatus}
                style={{ color: getGroupStatusColor(grupo.estado === 'ABIERTO') }}
              >
                {getGroupStatusIcon(grupo.estado === 'ABIERTO')}
              </span>
              <div className={extendedStyles.grupoDetalles}>
                <span className={extendedStyles.grupoCodigo}>{grupo.codigo_grupo || grupo.codigo || `Grupo ${grupo.id}`}</span>
                <span className={extendedStyles.grupoHorario}>ID: {grupo.id_grupo || grupo.id}</span>
                <span className={extendedStyles.grupoHorario}>{grupo.horario || 'Horario por definir'}</span>
                {(grupo.asesor || grupo.docente) && <span className={extendedStyles.grupoDocente}>Prof: {grupo.asesor || grupo.docente}</span>}
                <span className={extendedStyles.grupoCupo}>
                  Cupo: {grupo.cupo || `${grupo.inscritos || 0}/${grupo.cupo_maximo || 'N/A'}`}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={styles.detailContainer}>
      {/* Back Button */}
      <button className={styles.backButton} onClick={onBack}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M19 12H5"/>
          <path d="M12 19l-7-7 7-7"/>
        </svg>
        Volver a búsqueda
      </button>

      {/* Student Title */}
      <div className={styles.studentTitle}>
        <h2>Estudiante: {formatValue(student.nombre || student.nombre_completo)}</h2>
      </div>

      <div className={styles.detailLayout}>
        {/* Student Info Card */}
        <div className={styles.studentInfoCard}>
          <div className={styles.studentAvatar}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
            </svg>
          </div>
          
          <div className={styles.studentData}>
            <div className={styles.dataRow}>
              <span className={styles.dataLabel}>Matrícula</span>
              <span className={styles.dataValue}>{formatValue(student.matricula)}</span>
            </div>
            <div className={styles.dataRow}>
              <span className={styles.dataLabel}>Período actual</span>
              <span className={styles.dataValue}>{formatValue(student.periodoActual)}</span>
            </div>
            <div className={styles.dataRow}>
              <span className={styles.dataLabel}>Sección</span>
              <span className={styles.dataValue}>{formatValue(student.seccion)}</span>
            </div>
            <div className={styles.dataRow}>
              <span className={styles.dataLabel}>Institución</span>
              <span className={styles.dataValue}>{formatValue(student.institucion)}</span>
            </div>
            <div className={styles.dataRow}>
              <span className={styles.dataLabel}>Programa</span>
              <span className={styles.dataValue}>{formatValue(student.programa)}</span>
            </div>
            <div className={styles.dataRow}>
              <span className={styles.dataLabel}>Campus</span>
              <span className={styles.dataValue}>{formatValue(student.campus)}</span>
            </div>
          </div>
          
          <div className={styles.creditsSection}>
            <div className={styles.creditsLabel}>Total de créditos</div>
            <div className={styles.creditsValue}>
              {student.creditos_totales ? (
                <>
                  {student.creditos_acumulados ?? 0}/{student.creditos_totales}
                  <span className={styles.creditsPercentage}>
                    ({Number(student.porcentaje_avance ?? 0).toFixed(1)}%)
                  </span>
                </>
              ) : 'N/A'}
            </div>
          </div>

          {/* Schedule Button */}
          <button 
            className={styles.scheduleButton}
            onClick={() => setShowSchedule(true)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            Previsualizar horario
          </button>
        </div>

        {/* Materias Preasignadas / Plan */}
        <div className={extendedStyles.materiasSection}>
          <div className={extendedStyles.materiasHeader}>
            <div className={extendedStyles.materiasIcon}>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
              </svg>
            </div>
            <h3 className={extendedStyles.materiasTitle}>
              {student.materiasPreasignadas?.some(m => m.esDelPlan) 
                ? 'Materias del Plan de Estudios' 
                : 'Materias Preasignadas'}
            </h3>
          </div>

          {/* Mensajes de error y éxito */}
          {error && (
            <div className={extendedStyles.errorMessage}>
              <span>⚠️ {error}</span>
            </div>
          )}
          {successMessage && (
            <div className={extendedStyles.successMessage}>
              <span>✓ {successMessage}</span>
            </div>
          )}

          {/* Aviso de inscripción - solo informativo */}
          {!inscripcionActual && (
            <div className={extendedStyles.warningMessage}>
              <span>ℹ️ El estudiante no tiene inscripción activa. Se asignará sin inscripción asociada.</span>
            </div>
          )}

          {(!student.materiasPreasignadas || student.materiasPreasignadas.length === 0) ? (
            <div className={extendedStyles.noMaterias}>
              <p>No hay materias disponibles para asignación. Verifique que el plan de estudios tenga materias configuradas.</p>
            </div>
          ) : (
            <div className={extendedStyles.materiasTable}>
              <div className={extendedStyles.materiasTableHeader}>
                <div className={styles.headerCell}>Estado</div>
                <div className={styles.headerCell}>Materia</div>
                <div className={styles.headerCell}>Créditos</div>
                <div className={styles.headerCell}>Grupo Asignado</div>
                <div className={styles.headerCell}>Acciones</div>
              </div>
              
              <div className={extendedStyles.materiasTableBody}>
                {(() => {
                  const allMaterias = student.materiasPreasignadas;
                  const totalMaterias = allMaterias.length;
                  const needsPagination = totalMaterias > MATERIAS_PER_PAGE;
                  const totalPagesMaterias = Math.max(1, Math.ceil(totalMaterias / MATERIAS_PER_PAGE));
                  const safePageMaterias = Math.min(pageMaterias, totalPagesMaterias);
                  const startIdx = (safePageMaterias - 1) * MATERIAS_PER_PAGE;
                  const materiasPaginadas = needsPagination
                    ? allMaterias.slice(startIdx, startIdx + MATERIAS_PER_PAGE)
                    : allMaterias;

                  return (
                    <>
                {materiasPaginadas.map((materia) => {
                  const gruposParaMateria = getGruposParaMateria(materia.id_materia);
                  const isExpanded = expandedMaterias[materia.id_materia];
                  
                  return (
                    <React.Fragment key={materia.id}>
                      <div className={extendedStyles.materiaRow}>
                        <div className={extendedStyles.materiaCell}>
                          {materia.tieneGrupo ? (
                            <span className={extendedStyles.statusAssigned}>✓ Asignado</span>
                          ) : (
                            <span className={extendedStyles.statusPending}>○ Pendiente</span>
                          )}
                        </div>
                        <div className={extendedStyles.materiaCell}>
                          <span className={extendedStyles.materiaName}>{formatValue(materia.nombre)}</span>
                          {materia.clave && <span className={extendedStyles.materiaClave}>({materia.clave})</span>}
                        </div>
                        <div className={extendedStyles.materiaCell}>
                          <span className={extendedStyles.creditsBadge}>{formatValue(materia.creditos)}</span>
                        </div>
                        <div className={extendedStyles.materiaCell}>
                          {materia.tieneGrupo && materia.grupoAsignado ? (
                            <div className={extendedStyles.groupInfo}>
                              <span className={extendedStyles.groupId}>
                                {materia.grupoAsignado.grupo_codigo && materia.grupoAsignado.grupo_codigo !== 'N/A'
                                  ? materia.grupoAsignado.grupo_codigo
                                  : 'Grupo ya asignado'}
                              </span>
                              {materia.grupoAsignado.horario_texto && materia.grupoAsignado.horario_texto !== 'N/A' && (
                                <span className={extendedStyles.groupSchedule}>{materia.grupoAsignado.horario_texto}</span>
                              )}
                            </div>
                          ) : (
                            <span className={extendedStyles.noGroup}>Sin grupo asignado</span>
                          )}
                        </div>
                        <div className={extendedStyles.materiaCell}>
                          {materia.tieneGrupo && materia.grupoAsignado && (
                            <button 
                              className={extendedStyles.desasignarButton}
                              onClick={() => handleDesasignarClick(materia)}
                              disabled={loading}
                              title="Quitar asignación de este grupo"
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
                                <line x1="18" y1="6" x2="6" y2="18"/>
                                <line x1="6" y1="6" x2="18" y2="18"/>
                              </svg>
                              Desasignar
                            </button>
                          )}
                          {!materia.tieneGrupo && gruposParaMateria.length > 0 && (
                            <button 
                              className={extendedStyles.expandButton}
                              onClick={() => toggleMateriaExpansion(materia.id_materia)}
                            >
                              {isExpanded ? 'Ocultar grupos' : `Ver ${gruposParaMateria.length} grupo(s)`}
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ transform: isExpanded ? 'rotate(180deg)' : 'none' }}>
                                <path d="M6 9l6 6 6-6"/>
                              </svg>
                            </button>
                          )}
                          {!materia.tieneGrupo && gruposParaMateria.length === 0 && (
                            <span className={extendedStyles.noGroups}>Sin grupos disponibles</span>
                          )}
                        </div>
                      </div>
                      
                      {/* Grupos disponibles expandidos - Tabla responsive o tarjetas */}
                      {isExpanded && gruposParaMateria.length > 0 && (
                        renderGruposExpandidos(gruposParaMateria, materia)
                      )}
                    </React.Fragment>
                  );
                })}

                {needsPagination && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    borderTop: '1px solid #e0e0e0',
                    backgroundColor: '#fafafa',
                    borderRadius: '0 0 8px 8px',
                    marginTop: '4px'
                  }}>
                    <span style={{ fontSize: '13px', color: '#666' }}>
                      Mostrando {startIdx + 1}–{Math.min(startIdx + MATERIAS_PER_PAGE, totalMaterias)} de {totalMaterias} materias
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <button
                        onClick={() => setPageMaterias(1)}
                        disabled={safePageMaterias === 1}
                        style={{
                          padding: '4px 8px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          background: safePageMaterias === 1 ? '#f5f5f5' : '#fff',
                          cursor: safePageMaterias === 1 ? 'default' : 'pointer',
                          color: safePageMaterias === 1 ? '#bbb' : '#333',
                          fontSize: '13px'
                        }}
                      >
                        «
                      </button>
                      <button
                        onClick={() => setPageMaterias(p => Math.max(1, p - 1))}
                        disabled={safePageMaterias === 1}
                        style={{
                          padding: '4px 8px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          background: safePageMaterias === 1 ? '#f5f5f5' : '#fff',
                          cursor: safePageMaterias === 1 ? 'default' : 'pointer',
                          color: safePageMaterias === 1 ? '#bbb' : '#333',
                          fontSize: '13px'
                        }}
                      >
                        ‹
                      </button>
                      <span style={{ fontSize: '13px', color: '#333', padding: '0 8px' }}>
                        Página {safePageMaterias} de {totalPagesMaterias}
                      </span>
                      <button
                        onClick={() => setPageMaterias(p => Math.min(totalPagesMaterias, p + 1))}
                        disabled={safePageMaterias === totalPagesMaterias}
                        style={{
                          padding: '4px 8px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          background: safePageMaterias === totalPagesMaterias ? '#f5f5f5' : '#fff',
                          cursor: safePageMaterias === totalPagesMaterias ? 'default' : 'pointer',
                          color: safePageMaterias === totalPagesMaterias ? '#bbb' : '#333',
                          fontSize: '13px'
                        }}
                      >
                        ›
                      </button>
                      <button
                        onClick={() => setPageMaterias(totalPagesMaterias)}
                        disabled={safePageMaterias === totalPagesMaterias}
                        style={{
                          padding: '4px 8px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          background: safePageMaterias === totalPagesMaterias ? '#f5f5f5' : '#fff',
                          cursor: safePageMaterias === totalPagesMaterias ? 'default' : 'pointer',
                          color: safePageMaterias === totalPagesMaterias ? '#bbb' : '#333',
                          fontSize: '13px'
                        }}
                      >
                        »
                      </button>
                    </div>
                  </div>
                )}
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className={extendedStyles.actionButtons}>
        <button 
          className={extendedStyles.assignButton} 
          onClick={handleAssign}
          disabled={loading || gruposSeleccionados.length === 0}
        >
          {loading ? 'ASIGNANDO...' : `ASIGNAR (${gruposSeleccionados.length})`}
        </button>
        <button className={extendedStyles.cancelButton} onClick={handleCancel} disabled={loading}>
          CANCELAR
        </button>
      </div>

      {/* Modal de confirmación de desasignación */}
      {confirmDesasignar && (
        <div className={extendedStyles.modalOverlay}>
          <div className={extendedStyles.confirmModal}>
            <div className={extendedStyles.confirmModalIcon}>
              <svg viewBox="0 0 24 24" fill="none" stroke="#e65100" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 48, height: 48 }}>
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <h3 className={extendedStyles.confirmModalTitle}>Confirmar desasignación</h3>
            <p className={extendedStyles.confirmModalText}>
              ¿Está seguro que desea quitar la asignación del grupo
              {confirmDesasignar.grupoCodigo && confirmDesasignar.grupoCodigo !== 'N/A' 
                ? ` "${confirmDesasignar.grupoCodigo}"` 
                : ''} 
              de la materia <strong>{confirmDesasignar.materiaNombre}</strong>?
            </p>
            <p className={extendedStyles.confirmModalSubtext}>
              Esta acción se puede revertir volviendo a asignar el grupo.
            </p>
            <div className={extendedStyles.confirmModalActions}>
              <button 
                className={extendedStyles.confirmModalConfirm}
                onClick={handleConfirmDesasignar}
                disabled={loading}
              >
                {loading ? 'Procesando...' : 'Sí, desasignar'}
              </button>
              <button 
                className={extendedStyles.confirmModalCancel}
                onClick={() => setConfirmDesasignar(null)}
                disabled={loading}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Modal */}
      {showSchedule && (
        <ScheduleModal 
          student={student}
          gruposAsignados={student?.gruposAsignados || []}
          gruposDisponibles={gruposDisponibles}
          gruposSeleccionados={gruposSeleccionados}
          siguienteCiclo={siguienteCiclo}
          onClose={() => setShowSchedule(false)}
        />
      )}
    </div>
  );
}

export default StudentDetail;
